/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { ActionButton, DefaultButton, IButtonProps, PrimaryButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { IStackItemProps, Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import isEqual from 'lodash.isequal';
import omit from 'lodash.omit';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { PhraseListValuesContainer } from 'src/luis/modules/features/components/PhraseListValuesContainer';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';

export type PhraseListModalProps = BasicModalProps<PhraseList> & {
    /**
     * Initial data to populate the modal with.
     */
    initData?: PhraseList;

    /**
     * User role to determine the actions.
     */
    userRole: string;
    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    modalCreateTitle: {
        id: 'features.phraseListModal.modalCreateTitle',
        defaultMessage: 'Create new phrase list feature'
    },
    modalUpdateTitle: {
        id: 'features.phraseListModal.modalUpdateTitle',
        defaultMessage: 'Update phrase list feature'
    },
    nameLabel: {
        id: 'features.phraseListModal.nameLabel',
        defaultMessage: 'Name'
    },
    namePlaceholder: {
        id: 'features.phraseListModal.namePlaceholder',
        defaultMessage: 'Type phrase list feature name ...'
    },
    modeLabel: {
        id: 'features.phraseListModal.modeLabel',
        defaultMessage: 'These values are interchangeable'
    },
    searchButtonAriaLabel: {
        id: 'features.phraseListModal.searchButtonAriaLabel',
        defaultMessage: 'Search values'
    },
    search: {
        id: 'features.phraseListModal.search',
        defaultMessage: 'Search'
    },
    global: {
        id: 'features.phraseListModal.global',
        defaultMessage: 'Global'
    },
    isGlobalOn: {
        id: 'features.phraseListModal.isGlobalOn',
        defaultMessage: 'On'
    },
    isGlobalOff: {
        id: 'features.phraseListModal.isGlobalOff',
        defaultMessage: 'Off'
    },
    addValuesAnnounce: {
        id: 'core.phraseListModal.addValuesAnnounce',
        defaultMessage: 'Type in value and press enter'
    },
    createButton: {
        id: 'core.phraseListModal.createButton',
        defaultMessage: 'Create'
    },
    saveButton: {
        id: 'core.phraseListModal.saveButton',
        defaultMessage: 'Save'
    },
    cannotMakeAnyChanges: {
        id: 'core.phraseListModal.cannotMakeAnyChanges',
        defaultMessage: 'Your role {displayedUserRole} cannot make any changes in this page.'
    }
});

const { styleComponent } = getStylist('PhraseListModal');

const FlexStackItem = styleComponent<IStackItemProps>(Stack.Item)<{ flex: number }>('FlexStackItem', undefined, ({ customProps }) => ({
    flex: customProps.flex
}));
const FilterActionButton = styleComponent<IButtonProps>(ActionButton)('FilterActionButton', theme => ({
    height: theme.actionButtonHeight
}));

/**
 * Represents a modal for creating or updating phrase lists.
 *
 * @param props The modal input properties.
 */
export const PhraseListModal = (props: PhraseListModalProps) => {
    const { initData, onSubmit, onDismiss, userRole, displayedUserRole } = props;

    const { canWrite } = mapUserRoleToUserActions(userRole);

    const { localize } = useLocalization();

    // Controls the final dto that will be delivered back to the parent component.
    const { formState, formStateDto, updateFormState } = useFormState<PhraseList>({
        name: [initData.name, FormValidators.createRequiredValidator(), FormValidators.createBlacklistValidator()],
        phrases: [initData.phrases, FormValidators.createRequiredValidator({ deep: false })],
        isExchangeable: [initData.isExchangeable],
        isGlobal: [initData.isGlobal],
        isActive: [initData.isActive],
        id: [initData.id]
    });
    const [isSearchHidden, setIsSearchHidden] = React.useState(true);
    const [query, setQuery] = React.useState('');
    const [announcedMessage, setAnnouncedMessage] = React.useState('');

    // Flag whether the modal is of a new phrase list or an existing one.
    const isNew = !initData.id;
    const valuesChanged = React.useMemo(() => isEqual(omit(initData, 'usedBy'), formStateDto), [formStateDto, initData]);

    const filteredItems = formState.fields.phrases.value.filter(v => v.includes(query));

    /**
     * Combines the existing and newly added phrases to the values
     * modal state. Resets the field afterwards.
     *
     * @param addedPhrases The newly added phrases.
     */
    const onAddValues = (addedPhrases: string[]): void => {
        // Screen reader doesn't read the message if it's the same
        // so if the setTimeout is removed this gets announced once for
        // the first value and doesn't get announced again.
        // Note: Setting the empty string doesn't interrupt the screen reader.
        setAnnouncedMessage(`${localize(messages.addValuesAnnounce)}`);
        setTimeout(() => {
            setAnnouncedMessage('');
        }, 200);

        const filteredValues = addedPhrases.filter(
            p => !formState.fields.phrases.value.find(v => v.toLocaleLowerCase() === p.toLocaleLowerCase())
        );
        updateFormState({ phrases: [...formState.fields.phrases.value, ...filteredValues] });
    };

    /**
     * Removes the given values from the phrase list values.
     *
     * @param values The values to remove.
     */
    const onRemoveValues = (values: string[]): void => {
        const filteredValuesArray = formState.fields.phrases.value.filter(val => values.every(v => v !== val));
        updateFormState({ phrases: filteredValuesArray });
    };

    const renderFooter = () => (
        <>
            <PrimaryButton
                disabled={!formState.form.valid || (!isNew && valuesChanged) || !canWrite}
                text={localize(messages[isNew ? 'createButton' : 'saveButton'])}
                onClick={() => onSubmit(formStateDto)}
                styles={{ root: { marginRight: 8 }, rootDisabled: { cursor: 'not-allowed' } }}
                data-automation-id="submitFormButton"
            />
            <DefaultButton onClick={onDismiss} text={localize(globalMessages.cancel)} />
        </>
    );

    return (
        <BasicModal
            {...props}
            minWidth={900}
            valid={formState.form.valid}
            onRenderFooter={renderFooter}
            dialogContentProps={{ title: localize(isNew ? messages.modalCreateTitle : messages.modalUpdateTitle) }}>
            {!canWrite && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    {localize(messages.cannotMakeAnyChanges, { displayedUserRole })}
                </MessageBar>
            )}
            <Stack horizontal horizontalAlign="end">
                <Toggle
                    label={localize(messages.global)}
                    defaultChecked={initData.isGlobal}
                    ariaLabel={localize(messages.global)}
                    inlineLabel
                    onText={localize(messages.isGlobalOn)}
                    offText={localize(messages.isGlobalOff)}
                    onChange={(_, checked) => updateFormState({ isGlobal: checked })}
                    disabled={!canWrite}
                />
            </Stack>
            <Stack tokens={{ childrenGap: 20 }}>
                <LuisTextField
                    required
                    autoFocus
                    label={localize(messages.nameLabel)}
                    placeholder={localize(messages.namePlaceholder)}
                    errorMessage={formState.fields.name.errorMessage}
                    value={formState.fields.name.value}
                    onChange={(_, name) => updateFormState({ name })}
                    disabled={!canWrite}
                />

                <Stack tokens={{ childrenGap: 5 }}>
                    <FlexStackItem customProps={{ flex: 3 }}>
                        <Stack horizontal horizontalAlign="end">
                            <FilterActionButton
                                hidden={!isSearchHidden}
                                iconProps={{ iconName: 'Search' }}
                                onClick={() => setIsSearchHidden(false)}
                                ariaLabel={localize(messages.searchButtonAriaLabel)}
                                disabled={!(formState.fields.phrases.value.length > 0)}>
                                {localize(messages.search)}
                            </FilterActionButton>

                            {isSearchHidden ? null : (
                                <LuisTextField
                                    autoFocus
                                    underlined
                                    onChange={(_, q) => setQuery(q)}
                                    placeholder={localize(messages.search)}
                                    onBlur={() => setIsSearchHidden(true)}
                                />
                            )}
                        </Stack>

                        <PhraseListValuesContainer
                            onAddValues={onAddValues}
                            onRemoveItems={onRemoveValues}
                            items={filteredItems}
                            onRemoveLastItem={() => onRemoveValues([formState.fields.phrases.value.pop()])}
                            areValuesEditable={canWrite}
                        />
                        <div aria-live="assertive">
                            <SearchResultsAnnouncer isActive={query.length > 0} itemsLength={filteredItems.length} />
                        </div>
                    </FlexStackItem>
                </Stack>

                <Checkbox
                    label={localize(messages.modeLabel)}
                    checked={formState.fields.isExchangeable.value}
                    onChange={(_, isExchangeable) => updateFormState({ isExchangeable })}
                    disabled={!canWrite}
                />
            </Stack>

            <Announced aria-live="assertive" message={announcedMessage} />
        </BasicModal>
    );
};
